/** Read-only historical projection. Never use display keys as mutation targets. */
export interface CatalogueHistoryFields {
  id?: string;
  product_id?: string | null;
  variant_id?: string | null;
  catalogue_product_identity_id?: string | null;
  catalogue_variant_identity_id?: string | null;
  product_name?: string | null;
  product_name_snapshot?: string | null;
  sku?: string | null;
  sku_snapshot?: string | null;
  catalogue_snapshot_origin?: string | null;
  variant_attributes?: Record<string, string> | null;
  variant_options?: Record<string, string> | null;
  variant_attributes_snapshot?: Record<string, string> | null;
  products?: { name?: string | null; sku?: string | null } | null;
  variants?: { sku?: string | null; option_values?: Record<string, string> | null } | null;
}

function text(value: string | null | undefined): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

/** UUID attribution survives detachment; missing identities never group by SKU. */
export function historicalProductKey(row: CatalogueHistoryFields): string {
  const identity = text(row.catalogue_product_identity_id) ?? text(row.product_id);
  if (identity) return identity;
  if (!row.id) throw new Error("Historical row ID is required when catalogue identity is unknown");
  return `unidentified-row:${row.id}`;
}

export function historicalVariantId(row: CatalogueHistoryFields): string | null {
  return text(row.catalogue_variant_identity_id) ?? text(row.variant_id);
}

export function hasDetachedCatalogueIdentity(row: CatalogueHistoryFields): boolean {
  return Boolean((!row.product_id && row.catalogue_product_identity_id)
    || (!row.variant_id && row.catalogue_variant_identity_id));
}

export function historicalProductName(row: Omit<CatalogueHistoryFields, "variants">): string {
  const recorded = text(row.product_name);
  if (recorded) return recorded;
  const snapshot = text(row.product_name_snapshot);
  if (snapshot) {
    if (row.catalogue_snapshot_origin === "recorded_at_creation") return snapshot;
    if (row.catalogue_snapshot_origin === "last_known_catalogue") return `${snapshot} (last-known catalogue)`;
    return `${snapshot} (retained snapshot; timing unknown)`;
  }
  // A join from a newly-created same-SKU product must never label old history.
  const current = row.product_id && !row.catalogue_product_identity_id ? text(row.products?.name) : null;
  if (current) return `${current} (current catalogue; historical name unavailable)`;
  return "Historical product (name unavailable)";
}

export function historicalSku(row: CatalogueHistoryFields): string | null {
  const recorded = text(row.sku) ?? text(row.sku_snapshot);
  if (recorded) return recorded;
  if (hasDetachedCatalogueIdentity(row)) return null;
  return text(row.variants?.sku) ?? text(row.products?.sku);
}

/** Recorded option labels survive removal of the live variant relation. */
export function historicalVariantOptions(row: CatalogueHistoryFields): Record<string, string> | null {
  if (row.variant_attributes) return row.variant_attributes;
  if (row.variant_options) return row.variant_options;
  if (row.variant_attributes_snapshot) return row.variant_attributes_snapshot;
  if (hasDetachedCatalogueIdentity(row)) return null;
  return row.variants?.option_values ?? null;
}

/** Display/eligibility hint only; the database still authorizes every mutation. */
export function hasLiveCatalogueIdentity<T extends CatalogueHistoryFields>(row: T): row is T & { product_id: string } {
  return Boolean(text(row.product_id)) && !hasDetachedCatalogueIdentity(row);
}

/**
 * Resolve a return's source only within its already org/order-scoped candidates.
 * A SKU is a mutable label, not financial identity. Never fall back from an
 * explicit but unavailable source ID, nor match unrelated NULL catalogue IDs.
 */
export function matchHistoricalOrderLine<T extends CatalogueHistoryFields & { id: string; is_bundle?: boolean }>(
  item: CatalogueHistoryFields & { sales_order_item_id?: string | null }, candidates: readonly T[],
): T | null {
  const physical = candidates.filter((line) => !line.is_bundle);
  if (item.sales_order_item_id) {
    const explicit = physical.filter((line) => line.id === item.sales_order_item_id);
    return explicit.length === 1 ? explicit[0] : null;
  }
  const productId = text(item.catalogue_product_identity_id) ?? text(item.product_id);
  if (!productId) return null;
  const variantId = historicalVariantId(item);
  const matches = physical.filter((line) =>
    (text(line.catalogue_product_identity_id) ?? text(line.product_id)) === productId
    && historicalVariantId(line) === variantId);
  return matches.length === 1 ? matches[0] : null;
}
